import React from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";
import { isNativeApp } from "../../native/runtime";

export const APP_STORE_URLS = Object.freeze({
  ios: "https://apps.apple.com/app/id6794920231",
  android: "https://play.google.com/store/apps/details?id=net.vaultgrid.app",
});

export function AppleIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.54c-.02-2.45 2-3.64 2.09-3.7a4.49 4.49 0 0 0-3.54-1.92c-1.49-.16-2.94.89-3.7.89-.78 0-1.95-.87-3.22-.84a4.69 4.69 0 0 0-3.95 2.4c-1.72 2.98-.44 7.36 1.21 9.77.83 1.18 1.79 2.5 3.06 2.45 1.24-.05 1.7-.79 3.2-.79 1.48 0 1.91.79 3.2.76 1.34-.02 2.18-1.18 2.97-2.37a9.75 9.75 0 0 0 1.36-2.77 4.24 4.24 0 0 1-2.68-3.88ZM14.63 5.34a4.3 4.3 0 0 0 .98-3.09 4.38 4.38 0 0 0-2.85 1.47 4.1 4.1 0 0 0-1.01 2.97 3.62 3.62 0 0 0 2.88-1.35Z" />
    </svg>
  );
}

export function GooglePlayIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#00d7fe" d="M3.35 2.55A2 2 0 0 0 3 3.7v16.6c0 .43.13.82.35 1.15l9.46-9.45-9.46-9.45Z" />
      <path fill="#ffce00" d="m15.96 8.86-3.15 3.14 3.15 3.14 3.78-2.1c.79-.44.79-1.64 0-2.08l-3.78-2.1Z" />
      <path fill="#00f076" d="M3.35 2.55 12.81 12l3.15-3.14L5.1 2.82a2.02 2.02 0 0 0-1.75-.27Z" />
      <path fill="#ff3a44" d="m3.35 21.45 9.46-9.45 3.15 3.14L5.1 21.18a2.02 2.02 0 0 1-1.75.27Z" />
    </svg>
  );
}

export default function MobileAppInviteModal({
  open,
  platform,
  onClose,
  onDownload,
}) {
  const { t } = useTranslation("common");
  const storeUrl = APP_STORE_URLS[platform];
  const translationKey = platform === "android" ? "androidAppInvite" : "iosAppInvite";
  const StoreIcon = platform === "android" ? GooglePlayIcon : AppleIcon;

  // This prompt is exclusively for mobile users visiting the web platform.
  // Keep a component-level guard so it can never be shown inside a native
  // VaultGrid build even if it is accidentally mounted by a future controller.
  if (isNativeApp() || !storeUrl) return null;

  const handleDownload = () => {
    if (onDownload) {
      onDownload(storeUrl);
      return;
    }
    window.open(storeUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <AppModalShell
      open={open}
      title={t(`${translationKey}.title`)}
      onClose={onClose}
      closeAriaLabel={t("buttons.close")}
      maxWidthClassName="max-w-[34rem]"
      bodyClassName="pb-4"
      footer={(
        <div className="space-y-3">
          <AppModalButton onClick={handleDownload} className="flex items-center justify-center gap-2">
            <StoreIcon />
            <span>{t(`${translationKey}.downloadButton`)}</span>
          </AppModalButton>
          <AppModalButton onClick={onClose} variant="secondary">
            {t(`${translationKey}.laterButton`)}
          </AppModalButton>
        </div>
      )}
    >
      <AppModalContentItem className="flex flex-col items-center text-center">
        <img
          src={platform === "android" ? "/android-chrome-192x192.png" : "/apple-touch-icon.png"}
          alt="VaultGrid"
          className="h-auto w-[116px] object-contain"
        />

        <p className="mt-5 max-w-md text-[15px] font-medium leading-6 text-white/78">
          {t(`${translationKey}.description`)}
        </p>
      </AppModalContentItem>
    </AppModalShell>
  );
}
